import React, { useState, useRef, useEffect } from 'react';
// import { useTranslation } from 'react-i18next';

export interface TabItem {
  label: string;
  value: string;
}

export interface TabToggleProperties {
  tabs: (string | TabItem)[]; // List of tab names or complex items
  defaultTab?: string; // Optional default label or value
  onChange?: (value: string) => void; // Callback with the selected value
  isLabelValue?: boolean; // Prop to use label-value model
  maxVisible?: number; // Maximum number of tabs to show initially
}

/**
 * Tab toggle component
 */
function TabToggle({
  tabs,
  defaultTab,
  onChange,
  isLabelValue = false,
  maxVisible = 8,
}: TabToggleProperties) {
  const normalizedTabs: TabItem[] = React.useMemo(
    () =>
      tabs.map((tab) => (typeof tab === 'string' ? { label: tab, value: tab } : tab)),
    [tabs]
  );

  /**
   * Returns the identifier for a tab based on the isLabelValue prop.
   * @param tab - The tab item.
   * @returns The label or value string.
   */
  const getIdentifier = (tab: TabItem) => (isLabelValue ? tab.value : tab.label);

  const initialTab =
    normalizedTabs.find((t) => (isLabelValue ? t.value === defaultTab : t.label === defaultTab)) ||
    normalizedTabs[0];

  const [activeTabId, setActiveTabId] = useState<string>(getIdentifier(initialTab));
  const [underlineStyle, setUnderlineStyle] = useState<{ left: number; width: number }>({
    left: 0,
    width: 0,
  });

  const tabReferences = useRef<(HTMLButtonElement | null)[]>([]);
  const scrollContainerReference = useRef<HTMLDivElement>(null);

  const [maxWidth, setMaxWidth] = useState<number | string>('none');

  const hasOverflow = normalizedTabs.length > maxVisible;
  const remainingCount = hasOverflow ? normalizedTabs.length - maxVisible : 0;

  // Calculate width of first maxVisible tabs to restrict initial view
  useEffect(() => {
    if (hasOverflow) {
      /**
       * Calculates the total width of the first maxVisible tabs to restrict the initial view.
       */
      const calculateWidth = () => {
        let totalWidth = 0;
        for (let index = 0; index < maxVisible; index += 1) {
          const tab = tabReferences.current[index];
          if (tab) {
            totalWidth += tab.getBoundingClientRect().width;
          }
        }
        if (totalWidth > 0) {
          setMaxWidth(totalWidth);
        }
      };

      // Small timeout to ensure elements are measured correctly after layout
      const timer = setTimeout(calculateWidth, 100);
      return () => clearTimeout(timer);
    }
    setMaxWidth('none');
    return undefined;
  }, [hasOverflow, normalizedTabs, maxVisible]);

  // Adjust underline position dynamically
  useEffect(() => {
    const index = normalizedTabs.findIndex((t) => getIdentifier(t) === activeTabId);
    if (index === -1) return;

    const currentTab = tabReferences.current[index];
    const scrollContainer = scrollContainerReference.current;
    if (currentTab && scrollContainer) {
      setUnderlineStyle({
        left: currentTab.offsetLeft,
        width: currentTab.offsetWidth,
      });

      // Optional: Auto-scroll to active tab if it's out of view
      const tabLeft = currentTab.offsetLeft;
      const tabWidth = currentTab.offsetWidth;
      const containerScrollLeft = scrollContainer.scrollLeft;
      const containerWidth = scrollContainer.offsetWidth;

      if (tabLeft < containerScrollLeft) {
        scrollContainer.scrollTo({ left: tabLeft, behavior: 'smooth' });
      } else if (tabLeft + tabWidth > containerScrollLeft + containerWidth) {
        scrollContainer.scrollTo({ left: tabLeft + tabWidth - containerWidth, behavior: 'smooth' });
      }
    }
  }, [activeTabId, normalizedTabs, maxWidth]);

  // Handle onChange independently
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    onChange?.(activeTabId);
  }, [activeTabId, onChange]);

  // Sync state if defaultTab prop changes externally
  useEffect(() => {
    const found = normalizedTabs.find((t) =>
      isLabelValue ? t.value === defaultTab : t.label === defaultTab
    );
    if (found) {
      const foundId = getIdentifier(found);
      if (foundId !== activeTabId) {
        setActiveTabId(foundId);
      }
    }
  }, [defaultTab, isLabelValue]);

  /**
   * Handles the click event for the "Show more" badge to scroll the tab list.
   */
  const handleMoreClick = () => {
    if (scrollContainerReference.current) {
      const isAtEnd =
        scrollContainerReference.current.scrollLeft +
        scrollContainerReference.current.offsetWidth >=
        scrollContainerReference.current.scrollWidth - 10;

      scrollContainerReference.current.scrollTo({
        left: isAtEnd ? 0 : scrollContainerReference.current.scrollWidth,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="tab-toggle-wrap">
      <div
        className="tab-toggle"
        role="tablist"
        ref={scrollContainerReference}
        style={{ maxWidth }}>
        {normalizedTabs.map((tab, index) => {
          const tabId = getIdentifier(tab);
          return (
            <button
              key={tabId}
              type="button"
              role="tab"
              aria-selected={activeTabId === tabId}
              ref={(element) => {
                tabReferences.current[index] = element;
              }}
              className={`tab ${activeTabId === tabId ? 'active' : ''}`}
              onClick={() => setActiveTabId(tabId)}>
              {tab.label}
            </button>
          );
        })}
        <span
          className="underline"
          style={{ left: underlineStyle.left, width: underlineStyle.width }}
        />
      </div>
      {hasOverflow && (
        <button
          type="button"
          className="remaining-tabs-badge"
          onClick={handleMoreClick}
          title="Show more">
          +{remainingCount}
        </button>
      )}
    </div>
  );
}

export default TabToggle;

TabToggle.defaultProps = {
  defaultTab: '',
  onChange: null,
  isLabelValue: false,
  maxVisible: 8,
};
